import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Trash2, UserSquare } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { HideRetiredFilter } from "@/components/shared/hide-retired-filter";
import { NosLinkCell, NosLinkNote } from "@/components/shared/nos-link-cell";
import { ReorderButtons } from "@/components/shared/reorder-buttons";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type EmployeeProfile,
  useCreateEmployeeProfile,
  useDeleteEmployeeProfile,
  useEmployeeProfiles,
  useReorderEmployeeProfile,
  useUpdateEmployeeProfile,
} from "@/hooks/use-employee-profiles";
import { nosRowFor, useEmployeeProfileNosCheck } from "@/hooks/use-nos-link-check";
import { useRetiredRows } from "@/hooks/use-retired-rows";
import {
  type EmployeeProfileCreateInput,
  employeeProfileCreateSchema,
} from "@/schemas/employee-profiles";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; profile: EmployeeProfile }
  | { type: "delete"; profile: EmployeeProfile };

/**
 * Settings → Config → Employee profiles: the working-style profile a person is
 * described by (Builder / Connector / Craftsperson at the time of writing).
 *
 * The `employee_profile` table predates this page — it was seeded from NOS and
 * only ever read, so the list looked configurable without being editable. This
 * is the add/edit/retire surface, matching Employment types.
 */
export function SettingsEmployeeProfilesPage() {
  const { data: profiles = [], isLoading, isError, error, refetch } = useEmployeeProfiles();
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const deleteProfile = useDeleteEmployeeProfile();
  const reorder = useReorderEmployeeProfile();
  const { hideRetired, setHideRetired, active, hiddenCount, visible } = useRetiredRows(profiles);

  // Fetched separately from the list so the page renders from the database
  // straight away and the NOS column fills in when the gateway answers.
  const { data: nosCheck, isLoading: nosLoading } = useEmployeeProfileNosCheck();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load employee profiles"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        The working-style profile a person is described by, chosen on their profile. The order here
        is the order they're offered in. Retiring a profile hides it from the picker but keeps it
        readable on anyone who already has it and in their change history — a profile can only be
        deleted while nobody holds it.{" "}
        <span className="font-medium text-foreground">Profiles that came from NOS</span> stay linked
        to it: renaming one here changes only what Enable shows, and a profile added here has no NOS
        counterpart until one is created there, which means people using it can't be sent to NOS
        until it's linked.
      </p>

      <NosLinkNote check={nosCheck} loading={nosLoading} noun="profiles" />

      <DataToolbar
        filters={
          <HideRetiredFilter
            id="employee-profiles-hide-retired"
            checked={hideRetired}
            onCheckedChange={setHideRetired}
            hiddenCount={hiddenCount}
          />
        }
        actions={
          <Button onClick={() => setDialog({ type: "create" })}>
            <Plus className="mr-2 h-4 w-4" />
            Add profile
          </Button>
        }
      />

      <DataTable
        headers={[
          { label: "Order", className: "w-24" },
          { label: "Name" },
          { label: "NOS" },
          { label: "NOS ID" },
          { label: "People" },
          { label: "Status" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={UserSquare}
            title="No employee profiles"
            description="Add the first profile people can be described by."
          />
        }
      >
        {visible.map((p, index) => (
          <tr
            key={p.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3">
              {/* A retired row is pinned to the bottom by the API ordering, so its
                  position carries no meaning and it gets no arrows. The arrows on
                  the active rows above it are therefore always adjacent swaps. */}
              {p.isActive ? (
                <ReorderButtons
                  label={p.name}
                  disableUp={index === 0 || reorder.isPending}
                  disableDown={index === active.length - 1 || reorder.isPending}
                  onMove={(direction) => reorder.mutate({ id: p.id, direction })}
                />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className="px-4 py-3">
              <div className="font-medium">{p.name}</div>
              {p.description ? (
                <div className="max-w-md text-xs text-muted-foreground line-clamp-2">
                  {p.description}
                </div>
              ) : (
                <div className="text-xs italic text-muted-foreground/70">No description yet.</div>
              )}
            </td>
            <td className="px-4 py-3">
              {/* Whether a link is RECORDED. The NOS ID column beside it says
                  whether that link actually resolves in NOS — the two are
                  different questions, and only the second needs the gateway. */}
              <StatusPill
                tone={p.nosEmployeeProfileId === null ? "warning" : "info"}
                label={p.nosEmployeeProfileId === null ? "Not in NOS" : "Linked"}
              />
            </td>
            <td className="px-4 py-3">
              <NosLinkCell check={nosCheck} row={nosRowFor(nosCheck, p.id)} loading={nosLoading} />
            </td>
            <td className="px-4 py-3 text-muted-foreground">{p.personCount}</td>
            <td className="px-4 py-3">
              <StatusPill
                tone={p.isActive ? "success" : "neutral"}
                label={p.isActive ? "Active" : "Inactive"}
              />
            </td>
            <td className="px-4 py-3 text-right">
              <ActionCell
                items={[
                  {
                    icon: Pencil,
                    label: "Edit",
                    onClick: () => setDialog({ type: "edit", profile: p }),
                  },
                  {
                    icon: Trash2,
                    label: "Delete",
                    onClick: () => setDialog({ type: "delete", profile: p }),
                    variant: "destructive",
                    separatorBefore: true,
                    // NOS-linked profiles and profiles anyone still holds can
                    // only be deactivated — the server refuses either way, but
                    // there is no point offering an action that can't succeed.
                    disabled: p.nosEmployeeProfileId !== null || p.personCount > 0,
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      {(dialog.type === "create" || dialog.type === "edit") && (
        <EmployeeProfileDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          profile={dialog.type === "edit" ? dialog.profile : undefined}
        />
      )}

      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          title={`Delete "${dialog.profile.name}"?`}
          description="This removes the profile from the list. It can only be deleted while nobody holds it and it isn't linked to NOS — otherwise deactivate it instead, which hides it from the picker but keeps it readable on past records."
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            deleteProfile.mutate(dialog.profile.id, {
              onSuccess: () => setDialog({ type: "none" }),
            });
          }}
        />
      )}
    </div>
  );
}

function EmployeeProfileDialog({
  open,
  onOpenChange,
  profile,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile?: EmployeeProfile;
}) {
  const isEdit = profile != null;
  const create = useCreateEmployeeProfile();
  const update = useUpdateEmployeeProfile(profile?.id ?? "");
  const form = useForm<EmployeeProfileCreateInput>({
    resolver: zodResolver(employeeProfileCreateSchema),
    defaultValues: {
      name: profile?.name ?? "",
      description: profile?.description ?? null,
      isActive: profile?.isActive ?? true,
    },
  });

  async function onSubmit(values: EmployeeProfileCreateInput) {
    if (isEdit) {
      await update.mutateAsync({
        name: values.name,
        description: values.description ?? null,
        isActive: values.isActive,
      });
    } else {
      await create.mutateAsync(values);
    }
    form.reset();
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "Edit employee profile" : "Add employee profile"}
      description={
        isEdit
          ? "Update the name, the description readers see, or whether this profile can still be chosen."
          : "Add a new working-style profile people can be described by."
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel={isEdit ? "Save" : "Create"}
      pending={pending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Builder" {...field} />
            </FormControl>
            {isEdit && profile.nosEmployeeProfileId !== null && (
              <FormDescription>
                This profile is linked to NOS. Renaming it changes only what Enable shows — the link
                and NOS's own name are untouched.
              </FormDescription>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                rows={4}
                placeholder="What this profile means, in a sentence or two…"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormDescription>Shown to readers alongside the profile. Optional.</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="isActive"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value ?? true} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Available for selection</FormLabel>
              <FormDescription>
                Turn this off to retire the profile. It disappears from the picker but keeps
                rendering in history and on anyone who still holds it.
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}
